/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Modal, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Plus, ClipboardPaste, Check, ChefHat, Search, Download } from 'lucide-react';
import { modalActions, formActions } from '@/Actions';
import { notificationServices } from '@/Services';
import { getHigherUnitName, getHigherUnitAndQuantity } from '@/Helpers';
import cookingRecipesServices from '@/Services/Form Services/cookingRecipes.services';
import RecipeAccordion, { ROW_STATUS_STYLES } from './RecipeAccordion';
import AddProductSelectionModal from './AddProductSelectionModal';
import { parseExcelBlocks } from './PasteExcelBlockModal';
import ConfirmationModal from '@/Components/Modals/MultiPoModal/ConfirmationModal';
import { validator, deserializer } from './MultiRecipeEditModal.config';
import QuickProductCreateModal from './QuickProductCreateModal';
import { useProductData } from '@/Helpers';

const makeId = () => `rcp_${Math.random().toString(36).slice(2, 9)}`;
const makeRowId = () => `row_${Math.random().toString(36).slice(2, 9)}`;

const canonicalNameFor = (product, variant) => {
	const pName = product?.productName || '';
	if (!pName) return '';
	const vName = variant?.displayName && variant.displayName !== '-'
		? variant.displayName
		: '';
	const cvId = variant?.clientVariantId || '';
	const variantPart = vName ? ` (${vName})` : '';
	const clientIdPart = cvId ? ` — ${cvId}` : '';
	return `${pName}${variantPart}${clientIdPart}`;
};

const fuzzyScore = (a, b) => {
	const sa = (a || '').toLowerCase().trim();
	const sb = (b || '').toLowerCase().trim();
	if (!sa || !sb) return 0;
	if (sa === sb) return 1;
	if (sa.includes(sb) || sb.includes(sa)) {
		return Math.min(sa.length, sb.length) / Math.max(sa.length, sb.length);
	}
	const wa = sa.split(/\s+/);
	const wb = sb.split(/\s+/);
	const shortWords = wa.length <= wb.length ? wa : wb;
	const longSet = new Set(wa.length <= wb.length ? wb : wa);
	const hits = shortWords.filter((w) => longSet.has(w)).length;
	return shortWords.length ? hits / shortWords.length : 0;
};
const tokenize = (s) => String(s || '')
	.toLowerCase()
	.replace(/[^a-z0-9\s]+/g, ' ')
	.trim()
	.split(/\s+/)
	.filter(Boolean);

export const matchIngredientToProduct = (name, productList = []) => {
	let best = { score: 0, product: null, variant: null };
	for (const product of productList) {
		const variants = product?.variants?.length ? product.variants : [null];
		for (const variant of variants) {
			const candidates = [];
			const pName = product?.productName || '';
			const vName = variant?.displayName && variant.displayName !== '-'
				? variant.displayName
				: '';
			const cvId = variant?.clientVariantId || '';
			if (cvId) candidates.push(cvId);
			if (pName && vName && cvId) candidates.push(`${pName} (${vName}) — ${cvId}`);
			if (pName && vName) candidates.push(`${pName} (${vName})`);
			if (pName) candidates.push(pName);
			if (vName) candidates.push(vName);
			let variantBest = 0;
			for (const candidate of candidates) {
				const s = fuzzyScore(name, candidate);
				if (s > variantBest) variantBest = s;
			}
			if (variantBest > best.score) {
				best = { score: variantBest, product, variant };
			}
		}
	}

	const product = best.product;
	const variant = best.variant
		|| product?.variants?.find((v) => v?.isDefault)
		|| product?.variants?.[0]
		|| null;
	if (best.score >= 1) {
		return { status: 'matched', product, variant, score: best.score };
	}
	if (best.score >= 0.75) {
		return { status: 'partial', product, variant, score: best.score };
	}
	const nameTokens = tokenize(name);
	if (nameTokens.length >= 2) {
		const pasteFirstTwo = `${nameTokens[0]} ${nameTokens[1]}`;
		const firstTwoMatches = [];
		for (const p of productList) {
			const pTokens = tokenize(p?.productName);
			if (pTokens.length >= 2 && `${pTokens[0]} ${pTokens[1]}` === pasteFirstTwo) {
				firstTwoMatches.push(p);
			}
		}
		if (firstTwoMatches.length === 1) {
			const p = firstTwoMatches[0];
			const v = p?.variants?.find((vr) => vr?.isDefault)
				|| p?.variants?.[0]
				|| null;
			return {
				status: 'partial',
				product: p,
				variant: v,
				score: 0.7,
			};
		}
		if (firstTwoMatches.length > 1) {
			const candidateOptions = firstTwoMatches.flatMap((p) => (
				(p?.variants?.length ? p.variants : [null]).map((v) => ({
					productId: p?.id ?? null,
					variantId: v?.id ?? null,
					label: canonicalNameFor(p, v),
				}))
			));
			return {
				status: 'ambiguous',
				product: null,
				variant: null,
				score: 0.7,
				candidates: candidateOptions,
			};
		}
	}
	return { status: 'unmatched', product: null, variant: null, score: best.score };
};


const MultiRecipeEditModal = (props) => {
	const {
		show,
		data = {},
		toggleMultiRecipeEditModal,
	} = props;

	const {
		initialRecipes = [],
		externalClose = null,
	} = data;

	const productList = useSelector((state) => state?.form?.ALL_PRODUCTS?.data?.LIST) || [];
	const existingRecipesList = useSelector((state) => state?.form?.RECIPES?.data?.LIST) || [];
	const dispatch = useDispatch();

	const existingRecipeMap = useMemo(() => {
		const m = new Map();
		(existingRecipesList || []).forEach((r) => {
			if (r?.id != null && r?.type === 'RECIPE') {
				m.set(`${r.id}`, {
					version: r?.version,
					recipeStatus: r?.recipeStatus,
					productName: r?.productName,
				});
			}
		});
		return m;
	}, [existingRecipesList]);

	const [recipes, setRecipes] = useState([]);
	const [showProductPicker, setShowProductPicker] = useState(false);
	const [addRawMaterialFor, setAddRawMaterialFor] = useState(null);
	const [confirmModal, setConfirmModal] = useState(null);
	const [sectionQuery, setSectionQuery] = useState('');
	const [quickCreateState, setQuickCreateState] = useState({
		open: false,
		isRow: true,
		recipeId: null,
		rowId: null,
		name: '',
	});
	const pendingAutoLinkRef = useRef(null);

	const handleCreateProductForRow = ({ recipeId, rowId, name }) => {
		setQuickCreateState({
			open: true,
			isRow: true,
			recipeId,
			rowId,
			name: name || '',
		});
	};

	const handleCreateProductForRecipe = ({ recipeId, name }) => {
		setQuickCreateState({
			open: true,
			isRow: false,
			recipeId,
			rowId: null,
			name: name || '',
		});
	};

	const handleQuickCreateClose = () => {
		setQuickCreateState({
			open: false,
			isRow: true,
			recipeId: null,
			rowId: null,
			name: '',
		});
	};

	const handleQuickCreateSaved = () => {
		const { isRow, recipeId, rowId, name } = quickCreateState;
		const haveTarget = isRow ? !!rowId : !!recipeId;
		if (haveTarget && name) {
			pendingAutoLinkRef.current = { isRow, recipeId, rowId, name };
		}
		try {
			useProductData(dispatch);
		} catch (e) {
			console.error('Failed to refresh products after quick-create', e);
		}
	};

	useEffect(() => {
		const pending = pendingAutoLinkRef.current;
		if (!pending) return;
		if (!productList?.length) return;
		const m = matchIngredientToProduct(pending.name, productList);
		if ((m.status === 'matched' || m.status === 'partial') && m.product?.id) {
			if (pending.isRow) {
				changeRowProduct(pending.recipeId, pending.rowId, m.product, m.variant);
				notificationServices.generateNotification({
					type: 'success',
					message: `Linked the new product to your recipe row: "${m.product?.productName}".`,
				});
			} else {
				changeRecipeProduct(pending.recipeId, m.product);
				notificationServices.generateNotification({
					type: 'success',
					message: `Linked the new product to your recipe: "${m.product?.productName}".`,
				});
			}
			pendingAutoLinkRef.current = null;
		}
	}, [productList]);

	const visibleRecipes = useMemo(() => {
		const q = (sectionQuery || '').toLowerCase().trim();
		if (!q) return recipes;
		return recipes.filter((r) => (r?.name || '').toLowerCase().includes(q));
	}, [recipes, sectionQuery]);

	const orderedRecipes = useMemo(() => {
		const usedByMap = new Map();
		visibleRecipes.forEach((r) => {
			(r?.rows || []).forEach((row) => {
				if (row?.productId != null) {
					if (!usedByMap.has(row.productId)) usedByMap.set(row.productId, []);
					usedByMap.get(row.productId).push(r?.name || '');
				}
			});
		});
		const enriched = visibleRecipes.map((r) => {
			const usedIn = r?.productId != null
				? (usedByMap.get(r.productId) || []).filter((n) => n !== r.name)
				: [];
			const isIntermediate = r?.skuType === 'INTERMEDIATE';
			return {
				...r,
				_usedIn: isIntermediate ? usedIn : [],
				_isSfg: isIntermediate && usedIn.length > 0,
			};
		});

		const products = enriched.filter((r) => !r._isSfg);
		const sfgs = enriched.filter((r) => r._isSfg);
		const result = [...products];

		const placeSfg = (sfg) => {
			let lastIdx = -1;
			for (let i = 0; i < result.length; i++) {
				if ((sfg._usedIn || []).includes(result[i].name)) lastIdx = i;
			}
			if (lastIdx === -1) result.push(sfg);
			else result.splice(lastIdx + 1, 0, sfg);
		};
		sfgs.forEach(placeSfg);

		for (let pass = 0; pass < 2; pass++) {
			sfgs.forEach((sfg) => {
				const idx = result.indexOf(sfg);
				if (idx >= 0) result.splice(idx, 1);
				placeSfg(sfg);
			});
		}

		return result.sort((a, b) => {
			const ae = a?.alreadyExists ? 1 : 0;
			const be = b?.alreadyExists ? 1 : 0;
			return be - ae;
		});
	}, [visibleRecipes]);

	const recipeProductList = useMemo(
		() => (productList || []).filter((p) =>
			['PRODUCT', 'UNPACKED_PRODUCT', 'INTERMEDIATE'].includes(p?.skuType)
		),
		[productList]
	);

	const sectionExistingIds = useMemo(() => {
		const s = new Set();
		recipes.forEach((r) => {
			if (r?.productId != null) {
				s.add(`${r.productId}`);
			}
		});
		return s;
	}, [recipes]);

	const rawMaterialExistingIds = useMemo(() => {
		const s = new Set();
		const target = recipes.find((r) => r.id === addRawMaterialFor);
		(target?.rows || []).forEach((row) => {
			if (row?.productId != null && row?.variantId != null) {
				s.add(`${row.productId}::${row.variantId}`);
			}
		});
		return s;
	}, [recipes, addRawMaterialFor]);


	const ingredientProductList = useMemo(() => {
		const target = recipes.find((r) => r.id === addRawMaterialFor);
		const selfId = target?.productId;
		if (selfId == null) return productList;
		return (productList || []).filter((p) => String(p?.id) !== String(selfId));
	}, [productList, recipes, addRawMaterialFor]);

	useEffect(() => {
		if (show) {
			setShowProductPicker(false);
			if (!productList || productList.length === 0) {
				dispatch(
					formActions.gateway({
						entity: 'ALL_PRODUCTS',
						req: {
							params: {
								id: '',
								skuType: '',
								combo: true,
								variants: true,
							},
						},
						method: 'LIST',
					})
				);
			}
			if (!existingRecipesList || existingRecipesList.length === 0) {
				dispatch(
					formActions.gateway({
						entity: 'RECIPES',
						req: {
							params: {
								id: '',
								recipeStatus: '',
							},
						},
						method: 'LIST',
					})
				);
			}

		}
	}, [show]);

	if (!show) return null;

	const closeModal = (success = false) => {
		setRecipes([]);
		setShowProductPicker(false);
		setAddRawMaterialFor(null);
		setConfirmModal(null);
		setSectionQuery('');
		toggleMultiRecipeEditModal(false);
		if (externalClose) {
			externalClose(success === true);
		}
	};

	const toggleAccordion = (recipeId) => {
		setRecipes((prev) =>
			prev.map((r) => (r.id === recipeId ? { ...r, expanded: !r.expanded } : r))
		);
	};

	const addRecipe = () => {
		setShowProductPicker(true);
	};

	const handleProductPickerConfirm = ({ added = [], removedRowIds = [] } = {}) => {
		let existingAddedCount = 0;
		setRecipes((prev) => {
			const removedSet = new Set(removedRowIds);
			let next = prev.filter(
				(r) => !removedSet.has(`${r.productId}`)
			);
			added.forEach((row) => {
				const dupe = next.find((r) => r.productId === row?.productId);
				if (dupe) return;
				const sectionName = row?.productName;
				const existing = existingRecipeMap.get(`${row?.productId}`);
				if (existing) existingAddedCount += 1;
				next.push({
					id: makeId(),
					name: sectionName,
					expanded: true,
					totalProcessTime: 10,
					unit: getHigherUnitName(row?.unit),
					originalUnit: row?.unit,
					batchSize: 0,
					rows: [],
					productId: row?.productId,
					variantId: row?.variantId,
					skuType: row?.skuType,
					clientVariantId: row?.clientVariantId,
					categoryName: row?.categoryName,
					subCategoryName: row?.subCategoryName,
					alreadyExists: !!existing,
					existingVersion: existing?.version ?? null,
					existingRecipeStatus: existing?.recipeStatus ?? null,
				});
			});
			return next;
		});
		setShowProductPicker(false);
		const a = added.length;
		const r = removedRowIds.length;
		if (a || r) {
			const parts = [];
			if (a) parts.push(`${a} product recipe${a > 1 ? 's' : ''} added`);
			if (r) parts.push(`${r} removed`);
			notificationServices.generateNotification({
				type: 'success',
				message: parts.join(', ') + '.',
			});
		}
		if (existingAddedCount > 0) {
			notificationServices.generateNotification({
				type: 'warning',
				message: `${existingAddedCount} of these product${existingAddedCount > 1 ? 's' : ''} already ${existingAddedCount > 1 ? 'have' : 'has'} a recipe — saving will create a new version. Check the items marked "Already created" at the top.`,
			});
		}
	};

	const deleteRecipe = (recipeId) => {
		setConfirmModal({
			heading: 'Delete Product Recipe',
			message: 'Are you sure you want to delete this product recipe?',
			onConfirm: () => {
				setRecipes((prev) => prev.filter((r) => r.id !== recipeId));
			},
		});
	};

	const updateRecipeField = (recipeId, key, value) => {
		setRecipes((prev) =>
			prev.map((r) => {
				if (r.id !== recipeId) return r;
				const next = { ...r, [key]: value };
				if (key === 'batchSize' && r?._batchUnitMismatch) {
					next._batchUnitMismatch = null;
				}
				return next;
			})
		);
	};


	const addRow = (recipeId) => {
		setAddRawMaterialFor(recipeId);
	};

	const handleRawMaterialPickerConfirm = ({ added = [], removedRowIds = [] } = {}) => {
		if (!addRawMaterialFor) return;
		const removedSet = new Set(removedRowIds);
		setRecipes((prev) =>
			prev.map((r) => {
				if (r.id !== addRawMaterialFor) return r;
				let nextRows = (r.rows || []).filter(
					(row) => !removedSet.has(`${row.productId}::${row.variantId}`)
				);
				const presentKeys = new Set(
					nextRows
						.filter((x) => x?.productId != null && x?.variantId != null)
						.map((x) => `${x.productId}::${x.variantId}`)
				);
				const newRows = added
					.filter((vr) => !presentKeys.has(`${vr.productId}::${vr.variantId}`))
					.map((vr) => {
						const rowName = canonicalNameFor(vr?._product, vr?._variant) || vr?.productName;
						return {
							id: makeRowId(),
							name: rowName,
							baseQty: 0,
							unit: getHigherUnitName(vr?.unit),
							originalUnit: vr?.unit,
							containerWeight: 0,
							use: true,
							productId: vr?.productId,
							variantId: vr?.variantId,
							skuType: vr?.skuType,
							clientVariantId: vr?.clientVariantId,
						};
					});
				return { ...r, rows: [...nextRows, ...newRows] };
			})
		);
		setAddRawMaterialFor(null);
		const a = added.length;
		const rm = removedRowIds.length;
		if (a || rm) {
			const parts = [];
			if (a) parts.push(`${a} raw material${a > 1 ? 's' : ''} added`);
			if (rm) parts.push(`${rm} removed`);
			notificationServices.generateNotification({
				type: 'success',
				message: parts.join(', ') + '.',
			});
		}
	};

	const deleteRow = (recipeId, rowId) => {
		setRecipes((prev) =>
			prev.map((r) =>
				r.id === recipeId
					? { ...r, rows: r.rows.filter((row) => row.id !== rowId) }
					: r
			)
		);
	};

	const updateRow = (recipeId, rowId, key, value) => {
		setRecipes((prev) =>
			prev.map((r) =>
				r.id === recipeId
					? {
						...r,
						rows: r.rows.map((row) =>
							row.id === rowId ? { ...row, [key]: value } : row
						),
					}
					: r
			)
		);
	};

	const changeRowProduct = (recipeId, rowId, product, pickedVariant = null) => {
		if (!product) return;
		const variant = pickedVariant
			|| product?.variants?.find((v) => v?.isDefault)
			|| product?.variants?.[0]
			|| null;
		setRecipes((prev) =>
			prev.map((r) =>
				r.id === recipeId
					? {
						...r,
						rows: r.rows.map((row) =>
							row.id === rowId
								? {
									...row,
									name: canonicalNameFor(product, variant) || product?.productName,
									unit: getHigherUnitName(product?.unit),
									originalUnit: product?.unit,
									productId: product?.id,
									variantId: variant?.id ?? null,
									skuType: product?.skuType,
									clientVariantId: variant?.clientVariantId ?? null,
									matchStatus: 'userEdited',
								}
								: row
						),
					}
					: r
			)
		);
	};

	const changeRecipeProduct = (recipeId, product) => {
		if (!product) return;
		const variant = product?.variants?.find((v) => v?.isDefault)
			|| product?.variants?.[0]
			|| null;
		setRecipes((prev) =>
			prev.map((r) =>
				r.id === recipeId
					? {
						...r,
						name: canonicalNameFor(product, variant) || product?.productName || r.name,
						productId: product?.id,
						variantId: variant?.id ?? null,
						skuType: product?.skuType,
						unit: getHigherUnitName(product?.unit),
						originalUnit: product?.unit,
						clientVariantId: variant?.clientVariantId ?? null,
						matchStatus: 'userEdited',
					}
					: r
			)
		);
	};

	// Downloads a CSV template laid out exactly the way parseExcelBlocks
	// (PasteExcelBlockModal) expects a BOM block: "Product Name:" row starts a
	// block, the Sr No/Ingredients/Weight header starts the ingredient rows,
	// "Final Yield/ Output" carries the batch size, and the "FINAL COST" row
	// closes the block. Two stacked blocks show that multiple recipes can be
	// filled in one sheet and pasted together.
	const handleExportTemplate = () => {
		const rows = [
			['Bill of Materials'],
			['Doc no: PRD/BOM/01', 'Effective Date: 24/07/2024', '', 'Revision date & No'],
			['', 'Number of Batches: 1'],
			['1', 'Section Name: YOUR SECTION NAME', 'Product Name: Your Product Name'],
			['Sr No', 'Ingredients', 'Weight', 'UoM', 'Cost', 'Amount'],
			['1', 'Ingredient One', '15.6', 'KG', '320', '4999'],
			['2', 'Ingredient Two', '0.85', 'KG', '60', '51'],
			['3', 'Ingredient Three', '250', 'g', '', ''],
			['Final Yield/ Output', '', '16.5', 'Kg', 'Total', '5050'],
			['Tolerance', '', '', 'g'],
			['FINAL COST WITHOUT PACKAGING AND GAS (A)', '306.10'],
			[],
			['Bill of Materials'],
			['Doc no: PRD/BOM/02', 'Effective Date: 24/07/2024'],
			['', 'Number of Batches: 1'],
			['2', 'Section Name: SECOND SECTION', 'Product Name: Second Product Name'],
			['Sr No', 'Ingredients', 'Weight', 'UoM', 'Cost', 'Amount'],
			['1', 'Ingredient One', '2.5', 'KG', '', ''],
			['2', 'Ingredient Two', '500', 'ml', '', ''],
			['Final Yield/ Output', '', '3', 'Kg', 'Total', ''],
			['Tolerance', '', '', 'g'],
			['FINAL COST WITHOUT PACKAGING AND GAS (A)', ''],
		];
		const csv = rows
			.map((r) => r.map((c) => (/[",\n]/.test(c ?? '') ? `"${String(c).replace(/"/g, '""')}"` : (c ?? ''))).join(','))
			.join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.setAttribute('href', url);
		link.setAttribute('download', 'Recipe_BOM_Template.csv');
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const handlePasteFromExcel = async () => {
		if (!navigator?.clipboard?.readText) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Clipboard read is not supported in this browser. Try the latest Chrome/Edge.',
			});
			return;
		}
		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Reading clipboard…',
		});
		let raw;
		try {
			raw = await navigator.clipboard.readText();
		} catch (e) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Could not read clipboard. Allow clipboard access and copy the block again.',
				config: { id: loadingId },
			});
			return;
		}
		if (!raw || !raw.trim()) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Clipboard is empty. Copy Recipe block from Excel first.',
				config: { id: loadingId },
			});
			return;
		}
		const parsedBlocks = parseExcelBlocks(raw);
		if (!parsedBlocks.length) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'No valid Recipe block found in clipboard. Make sure your selection includes the "Product Name:" row and the ingredients table.',
				config: { id: loadingId },
			});
			return;
		}
		const unmatchedNames = [];
		const ambiguousNames = [];
		const convertedRows = [];
		let importedCount = 0;
		let overriddenCount = 0;

		setRecipes((prev) => {
			const next = [...prev];
			const pidKeyOf = (productId) => (productId != null ? `pid:${productId}` : null);
			const nameKeyOf = (name) => {
				const t = String(name || '').toLowerCase().trim();
				return t ? `name:${t}` : null;
			};
			const indexByKey = new Map();
			const registerEntry = (idx, productId, name) => {
				const pk = pidKeyOf(productId);
				const nk = nameKeyOf(name);
				if (pk) indexByKey.set(pk, idx);
				if (nk) indexByKey.set(nk, idx);
			};
			next.forEach((r, idx) => registerEntry(idx, r?.productId, r?.name));
			parsedBlocks.forEach((b) => {
				const sectionMatch = matchIngredientToProduct(b.productName, productList);
				const matchedProductId = sectionMatch.product?.id;
				importedCount += 1;
				if (sectionMatch.status === 'ambiguous') {
					ambiguousNames.push({
						name: b.productName,
						count: sectionMatch.candidates?.length || 0,
					});
				}
				const existing = matchedProductId != null
					? existingRecipeMap.get(`${matchedProductId}`)
					: null;
				const sectionCanonical = sectionMatch.product?.productName
				const sectionName = (sectionMatch.status === 'matched'
					|| sectionMatch.status === 'partial')
					&& sectionCanonical
					? sectionCanonical
					: (b.productName || 'Pasted Recipe');
				const isSectionMatched = sectionMatch.status === 'matched'
					|| sectionMatch.status === 'partial';
				const productUnit = sectionMatch.product?.unit || null;
				const familyOf = (u) => {
					if (!u) return '';
					const lc = String(u).toLowerCase().trim();
					if (['pc', 'count', 'piece', 'pieces', 'pcs'].includes(lc)) return 'pcs';
					return getHigherUnitName(lc) || lc;
				};
				const productFamily = familyOf(productUnit);
				const pastedFamily = familyOf(b.batchUnit);
				const displayUnit = isSectionMatched && productUnit
					? (getHigherUnitName(productUnit) || productUnit)
					: (getHigherUnitName(b.batchUnit) || b.batchUnit);
				const batchFromUnit = b.batchUnit || productUnit;
				const [convertedBatch] = getHigherUnitAndQuantity(
					batchFromUnit,
					Number(b.batchSize) || 0,
					true,
				);
				const batchSizeForRecipe = Number(convertedBatch) || 0;
				const batchUnitMismatch = (isSectionMatched
					&& productFamily
					&& pastedFamily
					&& productFamily !== pastedFamily)
					? {
						pastedUnit: b.batchUnit,
						productUnit: displayUnit,
					}
					: null;

				const builtRows = (b.ingredients || []).map((ing) => {
					const m = matchIngredientToProduct(ing.name, productList);
					if (m.status === 'unmatched') {
						unmatchedNames.push(ing.name);
					}
					if (m.status === 'ambiguous') {
						ambiguousNames.push({
							name: ing.name,
							count: m.candidates?.length || 0,
						});
					}
					const sourceUnit = ing?.unit || m?.product?.unit;
					const originalProductUnit = m?.product?.unit || ing?.unit;
					const [convertedQty, higherUnit] = getHigherUnitAndQuantity(
						sourceUnit,
						parseFloat(ing.weight) || 0,
						true,
					);
					if (
						higherUnit
						&& sourceUnit
						&& String(higherUnit).toLowerCase() !== String(sourceUnit).toLowerCase()
					) {
						convertedRows.push({
							name: ing.name,
							fromQty: ing.weight,
							fromUnit: sourceUnit,
							toQty: convertedQty,
							toUnit: higherUnit,
						});
					}
					const rowCanonical = canonicalNameFor(m.product, m.variant);
					const rowName = (m.status === 'matched' || m.status === 'partial')
						&& rowCanonical
						? rowCanonical
						: ing.name;
					const ingMatched = m.status === 'matched'
						|| m.status === 'partial';
					const ingProductHigherUnit = m?.product?.unit
						? (getHigherUnitName(m.product.unit) || m.product.unit)
						: null;
					const displayedRowUnit = ingMatched && ingProductHigherUnit
						? ingProductHigherUnit
						: (higherUnit || sourceUnit);
					return {
						id: makeRowId(),
						name: rowName,
						baseQty: convertedQty,
						unit: displayedRowUnit,
						originalUnit: originalProductUnit,
						containerWeight: 0,
						use: true,
						matchStatus: m.status,
						matchScore: m.score,
						productId: m.product?.id ?? null,
						variantId: m.variant?.id ?? null,
						skuType: m.product?.skuType ?? null,
						clientVariantId: m.variant?.clientVariantId ?? null,
						// Candidates surfaced for the 'ambiguous' state —
						// the row picker filters its options to these so
						// the user is shown only the products that share
						// the first two tokens with the pasted name.
						matchCandidates: m.status === 'ambiguous'
							? (m.candidates || [])
							: undefined,
					};
				})
					// A recipe's own product can't be its own ingredient — drop it.
					.filter((row) => !(matchedProductId != null && row?.productId != null
						&& String(row.productId) === String(matchedProductId)));

				const newRecipe = {
					id: makeId(),
					name: sectionName,
					expanded: true,
					totalProcessTime: 10,
					unit: displayUnit,
					originalUnit: productUnit || b.batchUnit || null,
					batchSize: batchSizeForRecipe,
					alreadyExists: !!existing,
					existingVersion: existing?.version ?? null,
					existingRecipeStatus: existing?.recipeStatus ?? null,
					rows: builtRows,
					productId: sectionMatch.product?.id ?? null,
					variantId: sectionMatch.variant?.id ?? null,
					skuType: sectionMatch.product?.skuType ?? null,
					matchStatus: sectionMatch.status,
					matchScore: sectionMatch.score,
					matchCandidates: sectionMatch.status === 'ambiguous'
						? (sectionMatch.candidates || [])
						: undefined,
					_source: 'excel-paste',
					_pastedBatchUnit: b.batchUnit,
					_pastedTolerance: b.tolerance,
					_pastedToleranceUnit: b.toleranceUnit,
					_batchUnitMismatch: batchUnitMismatch,
				};
				const pidKey = pidKeyOf(matchedProductId);
				const nameKey = nameKeyOf(b.productName);
				let existingIdx;
				if (pidKey) existingIdx = indexByKey.get(pidKey);
				if (existingIdx === undefined && nameKey) {
					existingIdx = indexByKey.get(nameKey);
				}
				if (existingIdx !== undefined) {
					const prevEntry = next[existingIdx];
					next[existingIdx] = {
						...newRecipe,
						id: prevEntry?.id ?? newRecipe.id,
						expanded: prevEntry?.expanded ?? newRecipe.expanded,
					};
					registerEntry(existingIdx, matchedProductId, b.productName);
					overriddenCount += 1;
				} else {
					next.push(newRecipe);
					registerEntry(next.length - 1, matchedProductId, b.productName);
				}
			});
			return next;
		});
		notificationServices.generateNotification({
			type: 'success',
			message: `${importedCount} product recipe${importedCount > 1 ? 's' : ''} imported from Excel${overriddenCount > 0 ? ` (${overriddenCount} overrode existing form entr${overriddenCount > 1 ? 'ies' : 'y'})` : ''}.`,
			config: { id: loadingId },
		});

		if (unmatchedNames.length > 0) {
			const sample = unmatchedNames.slice(0, 5).join(', ');
			const remaining = unmatchedNames.length - 5;
			setConfirmModal({
				isAlert: true,
				alertType: 'warning',
				heading: `${unmatchedNames.length} ingredient${unmatchedNames.length > 1 ? 's' : ''} not found in your products`,
				message: `Could not match: ${sample}${remaining > 0 ? ` and ${remaining} more` : ''}. Please pick the right product for each red row before saving.`,
			});
		}

		if (ambiguousNames.length > 0) {
			const sample = ambiguousNames
				.slice(0, 3)
				.map((a) => `${a.name} (${a.count} matches)`)
				.join('; ');
			const remaining = ambiguousNames.length - 3;
			notificationServices.generateNotification({
				type: 'warning',
				message: `${ambiguousNames.length} pasted name${ambiguousNames.length > 1 ? 's' : ''} matched multiple products — pick the right one for the blue rows. ${sample}${remaining > 0 ? `, +${remaining} more` : ''}.`,
			});
		}

		if (convertedRows.length > 0) {
			const sample = convertedRows
				.slice(0, 3)
				.map((c) => `${c.name}: ${c.fromQty} ${c.fromUnit} → ${c.toQty} ${c.toUnit}`)
				.join('; ');
			notificationServices.generateNotification({
				type: 'success',
				message: `${convertedRows.length} ingredient${convertedRows.length > 1 ? 's were' : ' was'} auto-converted to higher units (${sample}${convertedRows.length > 3 ? ', …' : ''}).`,
			});
		}
	};

	const performSave = async () => {
		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Saving recipes…',
		});
		try {
			const recipesPayload = recipes.map((recipe) => deserializer(recipe));
			const response = await cookingRecipesServices.bulkSaveRecipes({
				req: {
					data: {
						recipes: recipesPayload,
					},
				},
			});

			const statusCode = response?.statusCode || response?.responseCode;
			const isSuccess = statusCode === 200 || response?.status === 'Success';
			const isPartial = statusCode === 207 || response?.status === 'Partial';

			if (!isSuccess && !isPartial) {
				throw new Error(response?.message || 'Bulk save failed');
			}

			if (isPartial) {
				const { successCount = 0, failureCount = 0, results = [] } = response?.data || {};
				const failedResults = results.filter((r) => r.status !== 'Success');

				const failedDetails = failedResults.map((r) => {
					const recipe = recipes.find((rec) => rec.productId === r.productId);
					return recipe ? recipe.name : `Product ID: ${r.productId}`;
				});

				notificationServices.generateNotification({
					type: 'info',
					message: 'Save completed with some errors.',
					config: { id: loadingId },
				});

				setConfirmModal({
					isAlert: true,
					alertType: 'warning',
					heading: 'Partial Success',
					message: response?.message || `Saved ${successCount} recipes, ${failureCount} failed. Failed for: ${failedDetails.join(', ')}`,
				});
				const successProductIds = new Set(
					results.filter((r) => r.status === 'Success').map((r) => r.productId)
				);
				setRecipes((prev) => prev.filter((r) => !successProductIds.has(r.productId)));

			} else {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message || `${recipesPayload.length} recipe${recipesPayload.length > 1 ? 's' : ''} saved successfully.`,
					config: { id: loadingId },
				});
				closeModal(true);
			}
		} catch (err) {
			console.error('MultiRecipeEditModal bulk save failed:', err);
			notificationServices.generateNotification({
				type: 'error',
				message: err?.message || 'Failed to save recipes. Please try again.',
				config: { id: loadingId },
			});
		}
	};

	const handleSave = () => {
		const { isValid, error } = validator(recipes);
		if (!isValid) {
			notificationServices.generateNotification({
				type: 'error',
				message: error,
			});
			return;
		}
		const count = recipes.length;
		const existingRecipes = recipes.filter((r) => r?.alreadyExists);
		const newRecipesCount = count - existingRecipes.length;
		let heading = 'Save Product Recipes?';
		let message = `You're about to save ${count} product recipe${count > 1 ? 's' : ''}. Make sure all details are correct — this will create the recipes in the system. Do you want to continue?`;
		if (existingRecipes.length > 0) {
			const sample = existingRecipes
				.slice(0, 5)
				.map((r) => `"${r?.name || 'recipe'}"${r?.existingVersion != null ? ` (v${r.existingVersion} → v${Number(r.existingVersion) + 1})` : ''}`)
				.join(', ');
			const remaining = existingRecipes.length - 5;
			heading = `Update existing recipe${existingRecipes.length > 1 ? 's' : ''}?`;
			message = `${existingRecipes.length} of these product${existingRecipes.length > 1 ? 's' : ''} already ${existingRecipes.length > 1 ? 'have' : 'has'} a recipe in the system: ${sample}${remaining > 0 ? ` and ${remaining} more` : ''}. Saving will UPDATE ${existingRecipes.length > 1 ? 'them' : 'it'} — a new version will be created with status "Approval Pending" and the current approved version will stay live until the new one is approved.${newRecipesCount > 0 ? ` The remaining ${newRecipesCount} product recipe${newRecipesCount > 1 ? 's' : ''} will be created fresh.` : ''} Do you want to continue?`;
		}
		setConfirmModal({
			heading,
			message,
			onConfirm: () => {
				performSave();
			},
		});
	};

	return (
		<>
			<Modal
				open={show}
				onClose={(e, reason) => {
					if (reason === 'backdropClick') return;
					closeModal();
				}}
				aria-labelledby='multi-recipe-edit-modal'
			>
				<div
					className='absolute top-1/2 left-1/2 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden focus:outline-none'
					style={{
						width: '100%',
						height: '100%',
						transform: 'translate(-50%, -50%)',
					}}
				>
					{/* ===== HEADER (fixed) ===== */}
					<div className='shrink-0 flex justify-between items-center px-6 py-3 border-b border-slate-200 bg-white gap-4'>
						<div className='min-w-0'>
							<h3 className='text-lg font-bold text-slate-900 truncate'>
								Upload Recipes
							</h3>
							<p className='text-xs text-slate-500 mt-0.5'>
								Add product recipes, paste from Excel, and edit raw materials
							</p>
						</div>
						<div className='flex items-center gap-2.5 shrink-0'>
							<button
								type='button'
								onClick={addRecipe}
								className='flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-md bg-green-500 hover:bg-green-600 text-white transition-colors'
							>
								<Plus className='size-4' />
								Add New Product Recipe
							</button>
							<button
								type='button'
								onClick={handlePasteFromExcel}
								className='flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-md bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 transition-colors'
							>
								<ClipboardPaste className='size-4' />
								Paste Layout Block from Excel
							</button>
							<button
								type='button'
								onClick={handleExportTemplate}
								title='Download a CSV template in the BOM layout the paste feature understands — fill it in Excel, select the block, copy, then use Paste Layout Block'
								className='flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-md bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 transition-colors'
							>
								<Download className='size-4' />
								Export Template
							</button>
							<IconButton onClick={() => closeModal()} size='small'>
								<Close className='text-slate-500' />
							</IconButton>
						</div>
					</div>
					<div className='shrink-0 px-6 py-2.5 border-b border-slate-200 bg-white flex flex-wrap items-center gap-x-4 gap-y-2'>
						<span className='text-[11px] font-semibold text-slate-500 uppercase tracking-wider'>
							Match status:
						</span>
						{Object.entries(ROW_STATUS_STYLES).map(([key, s]) => (
							<span key={key} className='flex items-center gap-1.5 text-[11px] text-slate-600'>
								{s.chip && <span className={`inline-block w-3 h-3 rounded-sm ${s.chip}`} />}
								{s.icon && (
									<span className='inline-flex items-center justify-center size-3.5 rounded-full bg-blue-500 text-white text-[9px] font-bold'>
										{s.icon}
									</span>
								)}
								{s.label}
							</span>
						))}
						<span className='inline-block h-3 w-px bg-slate-300 mx-1' aria-hidden />
						<span className='flex items-center gap-1.5 text-[11px] text-slate-600'>
							<span className='inline-block w-3 h-3 rounded-sm bg-violet-200 border border-violet-400' />
							Already created (shown at top with version)
						</span>
						<div className='ml-auto relative w-64 max-w-full'>
							<Search className='size-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2' />
							<input
								type='text'
								value={sectionQuery}
								onChange={(e) => setSectionQuery(e.target.value)}
								placeholder='Search product recipes…'
								className='w-full pl-8 pr-3 py-1.5 text-[12px] border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-sky-500'
							/>
						</div>
					</div>

					<div className='flex-1 overflow-y-auto bg-slate-50 px-6 py-4'>
						{recipes.length > 0 && visibleRecipes.length === 0 && (
							<div className='flex flex-col items-center justify-center text-center py-12 text-slate-500 text-sm'>
								No product recipes match "<span className='font-semibold'>{sectionQuery}</span>".
							</div>
						)}
						{/* Recipe Accordions */}
						{recipes.length === 0 && (
							<div className='flex flex-col items-center justify-center text-center py-20 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50'>
								<div className='relative mb-6'>
									<div className='flex items-center justify-center w-20 h-20 bg-white rounded-[1.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-50'>
										<ChefHat className='w-10 h-10 text-blue-400 stroke-[1.5]' />
									</div>
									<div className='absolute -bottom-2 -right-2 flex items-center justify-center w-8 h-8 bg-yellow-400 rounded-lg shadow-sm transform rotate-12'>
										<Plus className='w-5 h-5 text-white stroke-[2.5]' />
									</div>
								</div>
								<h4 className='text-lg font-bold text-slate-600 mb-2'>No Product Recipes Yet</h4>
								<p className='text-sm text-slate-400 max-w-sm mx-auto'>
									Click "Add New Product Recipe" to start building your Recipes.
								</p>
							</div>
						)}
						{orderedRecipes.map((recipe) => (
							<RecipeAccordion
								key={recipe.id}
								recipe={recipe}
								productList={productList}
								recipeProductList={recipeProductList}
								onToggle={() => toggleAccordion(recipe.id)}
								onDelete={() => deleteRecipe(recipe.id)}
								onFieldChange={(key, value) => updateRecipeField(recipe.id, key, value)}
								onAddRow={() => addRow(recipe.id)}
								onDeleteRow={(rowId) => deleteRow(recipe.id, rowId)}
								onRowChange={(rowId, key, value) => updateRow(recipe.id, rowId, key, value)}
								onChangeRowProduct={(rowId, product, variant) => changeRowProduct(recipe.id, rowId, product, variant)}
								onChangeRecipeProduct={(product) => changeRecipeProduct(recipe.id, product)}
								onCreateProductForRow={handleCreateProductForRow}
								onCreateProductForRecipe={handleCreateProductForRecipe}
							/>
						))}
					</div>

					{/* ===== FOOTER (fixed) ===== */}
					<div className='shrink-0 flex justify-end items-center px-6 py-3 border-t-2 border-slate-200 bg-white'>
						<button
							type='button'
							onClick={handleSave}
							className='flex items-center gap-1.5 px-8 py-2.5 text-[13px] font-bold rounded-md bg-yellow-500 hover:bg-yellow-600 text-white border border-yellow-600 transition-colors'
						>
							<Check className='size-4' />
							Save Plan
						</button>
					</div>
				</div>
			</Modal>

			<AddProductSelectionModal
				show={showProductPicker}
				mode='product'
				list={productList}
				skuType={['PRODUCT', 'UNPACKED_PRODUCT', 'INTERMEDIATE']}
				existingIds={sectionExistingIds}
				heading='Add Product Recipes — Select Products'
				confirmLabel='Add Product Recipes'
				onClose={() => setShowProductPicker(false)}
				onConfirm={handleProductPickerConfirm}
			/>
			<AddProductSelectionModal
				show={addRawMaterialFor !== null}
				mode='ingredient'
				list={ingredientProductList}
				skuType={null}
				existingIds={rawMaterialExistingIds}
				heading='Add Raw Materials — Select Variants'
				subtitle='Pick any SKU type. Already-added items are pre-checked.'
				confirmLabel='Add Materials'
				onClose={() => setAddRawMaterialFor(null)}
				onConfirm={handleRawMaterialPickerConfirm}
			/>
			<ConfirmationModal
				isOpen={confirmModal !== null}
				isAlert={!!confirmModal?.isAlert}
				alertType={confirmModal?.alertType || 'info'}
				showCrossBtn={!!confirmModal?.isAlert}
				heading={confirmModal?.heading || ''}
				message={confirmModal?.message || ''}
				onClose={() => setConfirmModal(null)}
				onConfirm={confirmModal?.onConfirm
					? () => {
						const cb = confirmModal.onConfirm;
						setConfirmModal(null);
						cb();
					}
					: undefined}
			/>
			<QuickProductCreateModal
				open={quickCreateState.open}
				initialName={quickCreateState.name}
				onClose={handleQuickCreateClose}
				onSaved={handleQuickCreateSaved}
			/>
		</>
	);
};

const mapStateToProps = (state) => {
	const { show, data } = state.modal.multiRecipeEditModal || { show: false, data: {} };
	return { show, data };
};

const mapDispatchToProps = {
	toggleMultiRecipeEditModal: modalActions.toggleMultiRecipeEditModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(MultiRecipeEditModal);
